import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import {
  Container,
  Header,
  Pagination,
  Divider,
  Loader
} from "semantic-ui-react";

import RedirectWithReset from "../../components/utils/RedirectWithReset.jsx";
import UnauthorisedFeature from "../../components/auth/UnauthorisedFeature.jsx";
import InvitePanel from "../../components/invites/invitePanel/InvitePanel.jsx";
import InvitesFilters from "../../components/invites/InvitesFilters.jsx";

import withConfirmationModal from "../../components/modals/withConfirmationModal.js";

import { getInvites } from "../../state/reducers/invites/invitesActions.js";
import { getAccount } from "../../state/reducers/account/accountActions.js";
import { coreFullRefreshAction } from "../../state/reducers/core/coreActions.js";
import { inviteOperationAction } from "../../state/reducers/invites/invitesActions.js";
import {
  getURLFilterParams,
  updateFilterSearchParams,
  updatePageSearchParams
} from "../../utils/utils.js";

import { OPERATIONS } from "../../constants/operations.js";
import { AT_TYPES } from "../../constants/entity.js";

import inviteSelector from "../../state/reducers/invites/inviteSelector.js";
import endpointSelector from "../../state/reducers/endpoints/endpointSelector.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class Invites extends Component {
  static propTypes = {
    copyKey: PropTypes.string.isRequired,

    invites: PropTypes.array,
    dispatchGetInvites: PropTypes.func.isRequired,
    isFetching: PropTypes.bool.isRequired,
    listInviteAvailability: PropTypes.object.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    copyKey: "invites"
  };

  state = {
    filters: null,
    view: "row",
    modalAction: null,
    redirect: null
  };

  // -- PAGE DATA MANAGEMENT -- //

  static getDerivedStateFromProps(props, state) {
    const { location } = props;

    const redirect = state.redirect === location.search ? null : state.redirect;

    return {
      ...state,
      filters: getURLFilterParams(location),
      redirect
    };
  }

  componentDidMount = () => {
    this.getData();
  };

  componentDidUpdate(prevProps) {
    // do search params change
    if (this.props.location.search !== prevProps.location.search) {
      this.getData();
    }
  }

  getData = () => {
    const {
      location,
      isFetching,
      isErrored,
      dispatchGetInvites,
      listInviteAvailability: { available }
    } = this.props;

    if (!isFetching && !isErrored && available) {
      dispatchGetInvites(location.search, true);
    }
  };

  // -- HANDLERS & ACTIONS -- //

  onFilterChange = data => {
    const redirect = updateFilterSearchParams(
      this.props.location.search,
      data.filters
    ).toString();

    this.setState({
      view: data.view,
      filters: data.filters,
      redirect: `?${redirect}`
    });
  };

  onPageChange = (evt, { activePage }) => {
    const redirect = updatePageSearchParams(
      this.props.location.search,
      activePage
    ).toString();

    this.setState({
      redirect: `?${redirect}`
    });
  };

  // -- MODAL -- //

  createOptionsWithSuccess = options => {
    const { location } = this.props;

    return {
      ...options,
      success: [
        getAccount(),
        coreFullRefreshAction(),
        getInvites(location.search)
      ],
      autoComplete: false
    };
  };

  handleModal = (invite, operationName, data, options) => {
    const { openModal } = this.props;

    const modalAction = inviteOperationAction(
      invite,
      operationName,
      data,
      this.createOptionsWithSuccess(options)
    );

    const modalCopyKey = `invite.modals.${operationName}_modal`;

    this.setState({ modalAction }, () => {
      openModal(this.doModalAction, modalCopyKey);
    });
  };

  doModalAction = () => {
    this.props.dispatchAction(this.state.modalAction);
  };

  doOperationAction = (invite, operationName, data, options) => {
    this.props.dispatchAction(
      inviteOperationAction(
        invite,
        operationName,
        data,
        this.createOptionsWithSuccess(options)
      )
    );
  };

  // -- RENDER -- //

  renderHeaderSet() {
    const { view, filters } = this.state;
    const { copy } = this.props;
    return (
      <Fragment>
        <InvitesFilters
          onFilterChange={this.onFilterChange}
          initialValues={{ filters, view }}
          copyKey="filters"
        />
        <Header as="h3">{copy("invites.title")}</Header>
      </Fragment>
    );
  }

  renderInvites = () => {
    const { invites } = this.props;

    return invites.map(invite => {
      return (
        <InvitePanel
          key={invite["@id"]}
          invite={invite}
          onAccept={this.doOperationAction}
          onReject={this.handleModal}
          onDelete={this.handleModal}
        />
      );
    });
  };

  render() {
    const { redirect } = this.state;
    const {
      invites,
      page,
      totalPages,
      isFetching,
      listInviteAvailability: { permitted, enabled },
      copy
    } = this.props;

    if (redirect) {
      return (
        <RedirectWithReset to={`${location.pathname}${redirect}`} push={true} />
      );
    }

    if (!permitted) {
      return <UnauthorisedFeature title={copy("invites.title")} />;
    }

    // early return if project isn't ready to render
    if (isFetching) {
      return (
        <Container>
          {this.renderHeaderSet()}
          <Loader active inline="centered" />
        </Container>
      );
    }

    // early return if no invites are available
    if (!enabled || !invites || invites.length < 1) {
      return (
        <Container className="main-responsive">
          {this.renderHeaderSet()}
          <p>{copy("invites.no_invites")}</p>
        </Container>
      );
    }

    // render invites
    return (
      <Container className="main-responsive">
        {this.renderHeaderSet()}

        {this.renderInvites()}

        {totalPages && totalPages > 1 && (
          <Fragment>
            <Pagination
              defaultActivePage={page}
              totalPages={totalPages}
              firstItem={null}
              lastItem={null}
              onPageChange={this.onPageChange}
              className={"float-right"}
            />
            <Divider hidden fitted clearing />
          </Fragment>
        )}
      </Container>
    );
  }
}

const mapStateToProps = (state, props) => ({
  invites: inviteSelector.selectInvites()(state, props),
  page: state.invites.page,
  totalPages: state.invites.totalPages,
  isFetching: state.invites.isFetching,
  listInviteAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.INVITE,
    OPERATIONS.LIST
  ),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  dispatchGetInvites: (...args) => dispatch(getInvites(...args)),
  dispatchAction: action => dispatch(action)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withConfirmationModal(Invites));
